import type {
    NavigationGuard,
    NavigationGuardNext,
    RouteRecordRaw,
    RouterScrollBehavior,
    useRoute,
} from 'vue-router';
import { createRouter, createWebHistory } from 'vue-router';
import LoginView from '@/views/LoginView.vue';
import PreferencesView from '@/views/preferences/PreferencesView.vue';
import CaseSettingsView from '@/views/case-settings/CaseSettingsView.vue';
import ScansView from '@/views/dicoms/ScanView.vue';
import PlansView from '@/views/plan/PlansView.vue';
import PlannerView from '@/views/3d-planner/PlannerView.vue';
import PrivacyView from '@/views/PrivacyView.vue';
import TermsView from '@/views/TermsView.vue';
import AboutView from '@/views/AboutView.vue';
import ForgotPasswordView from '@/views/forgot-password/ForgotPasswordView.vue';
import ResetPasswordView from '@/views/reset-password/ResetPasswordView.vue';
import CaseSearchView from '@/views/dashboard/CaseSearchView.vue';
import { cachedAuthTokens } from '@/api/cache';
import AdminView from '@/views/admin/AdminView.vue';
import { useUserStore } from '@/stores/userStore';
import { identifyUser } from '@/plugins/amplitude';
import { assertNonNull } from '@/util/assert';
import { taggedLogger } from '@/util/taggedLogger';
import type { ApiUserObject } from '@/api/me';
import ThemeView from '@/views/admin/ThemeView.vue';
import { IN_MAINTENANCE_MODE, MAINTENANCE_REDIRECT } from '@/plugins/featureFlags/maintenanceMode';
import { useOrganisationProfile } from '@/stores/organisationProfile/store';
import { useAmplitudeFeatures } from '@/stores/featureToggles/amplitudeFeaturesStore';
import { verify } from '@/util/verify';
import PrototypeView from '@/views/prototype/PrototypeLandmarksView.vue';

type Route = ReturnType<typeof useRoute>;

const logger = taggedLogger('router');

export enum ROUTES {
    HOME = 'HOME',
    LOGIN = 'LOGIN',
    PREFERENCES = 'PREFERENCES',
    NEW_CASE = 'NEW_CASE',
    EDIT_CASE = 'EDIT_CASE',
    DICOMS = 'DICOMS',
    PLANNER = 'PLANNER',
    PLANS = 'PLANS',
    ABOUT = 'ABOUT',
    TERMS = 'TERMS',
    PRIVACY = 'PRIVACY',
    FORGOT_PASSWORD = 'FORGOT_PASSWORD',
    RESET_PASSWORD = 'RESET_PASSWORD',
    ADMIN = 'ADMIN',
    THEME = 'THEME',
    PROTOTYPE = 'PROTOTYPE',
}

export const routes: RouteRecordRaw[] = [
    /**
     * These routes don't require the user to be logged in.
     */
    { path: '/login', name: ROUTES.LOGIN, component: LoginView },
    { path: '/forgot-password', name: ROUTES.FORGOT_PASSWORD, component: ForgotPasswordView },
    { path: '/reset-password', name: ROUTES.RESET_PASSWORD, component: ResetPasswordView },

    /**
     * Must be logged in to access the following routes.
     *
     * User will be redirected to login page if they are not logged in.
     */
    {
        path: '/',
        name: ROUTES.HOME,
        component: CaseSearchView,
        meta: { requiresAuth: true },
    },
    {
        path: '/preferences',
        name: ROUTES.PREFERENCES,
        component: PreferencesView,
        meta: { requiresAuth: true },
    },
    {
        path: '/case/new',
        name: ROUTES.NEW_CASE,
        component: CaseSettingsView,
        meta: { requiresAuth: true },
    },
    {
        path: '/case/:id/edit',
        name: ROUTES.EDIT_CASE,
        component: CaseSettingsView,
        meta: { requiresAuth: true, caseSpecific: true },
    },

    /**
     * These routes are case specific, meaning the page content refers to a particular case.
     *
     * Routes that are case specific have the case navigation in the application header.
     */
    {
        path: '/case/:id/dicoms',
        name: ROUTES.DICOMS,
        component: ScansView,
        meta: { requiresAuth: true, caseSpecific: true },
    },
    {
        path: '/case/:id/planner',
        name: ROUTES.PLANNER,
        component: PlannerView,
        meta: { requiresAuth: true, caseSpecific: true },
    },
    {
        path: '/case/:id/plans',
        name: ROUTES.PLANS,
        component: PlansView,
        meta: { requiresAuth: true, caseSpecific: true },
    },

    /**
     * Static pages accessible from the sidebar.
     */
    {
        path: '/about',
        name: ROUTES.ABOUT,
        component: AboutView,
        meta: { requiresAuth: true },
    },
    {
        path: '/terms-of-use',
        name: ROUTES.TERMS,
        component: TermsView,
        meta: { requiresAuth: true },
    },
    {
        path: '/privacy-policy',
        name: ROUTES.PRIVACY,
        component: PrivacyView,
        meta: { requiresAuth: true },
    },

    {
        path: '/admin/case/:id/',
        name: ROUTES.ADMIN,
        component: AdminView,
        beforeEnter: _verifyAdmin,
        meta: { requiresAuth: true, caseSpecific: true },
    },
    {
        path: '/admin/theme',
        name: ROUTES.THEME,
        component: ThemeView,
        beforeEnter: _verifyAdmin,
        meta: { requiresAuth: true },
    },
    {
        path: '/admin/prototype',
        name: ROUTES.PROTOTYPE,
        component: PrototypeView,
        beforeEnter: _verifyAdmin,
        meta: { requiresAuth: true },
    },
];

export function plannerRoute(caseId: number) {
    return {
        name: ROUTES.PLANNER,
        params: {
            id: caseId,
        },
    };
}

export function adminCaseRoute(caseId: number) {
    return {
        name: ROUTES.ADMIN,
        params: {
            id: caseId,
        },
    };
}

/**
 * Get the current case-id from the route.
 */
export function caseIdFromRoute(route: Route): number {
    const caseIdParam = verify(route.params.id, 'Case id is required');
    return Number(caseIdParam);
}

const scrollBehavior: RouterScrollBehavior = (_to, _from, savedPosition) => {
    if (savedPosition) {
        return savedPosition;
    } else {
        return { top: 0 };
    }
};

export const router = createRouter({
    history: createWebHistory(import.meta.env.BASE_URL),
    routes,
    scrollBehavior,
});
export default router;

/**
 * Implements software requirement: H1SR-68
 *
 * @link https://formuslabs.youtrack.cloud/issue/H1SR-68/User-can-authenticate-with-their-email-as-a-username-and-their-chosen-password
 *
 * @see user cannot perform any other functionality in the software while unauthenticated
 */

export const requireAuthentication: NavigationGuard = async (to, from, next) => {
    const userStore = useUserStore();
    const orgProfile = useOrganisationProfile();

    // No authentication required for this route
    if (!to.meta.requiresAuth) {
        // This ensures that user store is empty when user is logged out.
        logger.debug("Clear the user data if it's a public route.");
        userStore.reset();
        orgProfile.clear();

        return next();
    }

    // it's fine if the user has a valid refresh token,
    // we can use that to automatically obtain a new access token if needed.
    const tokens = await cachedAuthTokens();
    if (!tokens) {
        logger.debug('Could not automatically authenticate user, redirecting to login page');
        return next({ name: ROUTES.LOGIN });
    }

    if (!userStore.user) {
        logger.info('Loading current user. This should be done only once per session.');
        await userStore.load();

        if (!userStore.user) {
            logger.info('Failed to load user data. Redirecting to login page.');
            return next({ name: ROUTES.LOGIN });
        }

        const user = assertNonNull<ApiUserObject | null>(userStore.user, 'user');

        await orgProfile.load(user.organisation.id);
        await identifyUser(user);
        // Re-resolve the Amplitude toggles now the session has an identified user, so
        // cohort-targeted flags stop resolving against the anonymous device id. This is the one
        // place where both halves of a dual gate are populated for the session.
        //
        // Deliberately NOT awaited: refreshes are serialised, so this one can be queued behind
        // the bootstrap start() and would then block the first navigation on a third-party
        // analytics service being reachable. Navigation must never be gated on that. Resolving
        // after the planner has mounted is safe because useHipSpineLoadingViewModel re-attempts
        // the study load when the composed gate transitions off -> on. refresh() never rejects,
        // so `void` cannot strand an unhandled rejection.
        void useAmplitudeFeatures().refresh();
    } else {
        logger.debug('User already logged in. No need to load user data.');
    }

    return next();
};

export const maintenanceRedirect: NavigationGuard = async (to, from, next) => {
    if (IN_MAINTENANCE_MODE && MAINTENANCE_REDIRECT !== '') {
        logger.info('IN_MAINTENANCE_MODE: %s', IN_MAINTENANCE_MODE);
        logger.info('MAINTENANCE_REDIRECT: %s', MAINTENANCE_REDIRECT);
        window.location.href = MAINTENANCE_REDIRECT;
    } else {
        next(); // Proceed to the requested route
    }
};

router.beforeEach(maintenanceRedirect);
router.beforeEach(requireAuthentication);

// Declared as a hoisted function (not a `const`) because the `routes` table above references it
// at module-evaluation time. Its parameters take their types from `NavigationGuard` rather than
// being annotated loosely, so the signature stays checked.
async function _verifyAdmin(
    _to: Parameters<NavigationGuard>[0],
    _from: Parameters<NavigationGuard>[1],
    next: NavigationGuardNext,
): Promise<boolean | void> {
    const store = useUserStore();
    await store.load();

    if (store.isAdmin) {
        return next();
    }

    return false;
}
